import {Field, Formik} from "formik";
import {offerStatus} from "../../../utils/constants";
import * as Yup from "yup";
import {useState} from "react";
import {useTranslation} from "react-i18next";

const FilterCard = ({
  setFilter,
  setFilterTouched,
  className,
  initialFilter,
  sectors = [],
}) => {
  const [customErrors, setCustomErrors] = useState({});
  const {t} = useTranslation();

  const formSchema = Yup.object().shape({
    minBudget: Yup.number()
      .nullable()
      .min(0, "Doit être un nombre positif")
      .typeError("Doit être un nombre"),

    maxBudget: Yup.number()
      .nullable()
      .min(0, "Doit être un nombre positif")
      .typeError("Doit être un nombre")
      .when("minBudget", (minBudget, schema) => {
        return minBudget !== null
          ? schema.min(
              minBudget,
              "Le budget maximum doit être supérieur au budget minimum"
            )
          : schema;
      }),
  });

  return (
    <Formik
      validationSchema={formSchema}
      initialValues={{
        selectedAll: true,
        selectedSector: [],
        selectedStatus: [offerStatus.PUBLIC],
        minBudget: "",
        maxBudget: "",
        selectedOrder: "DESC",
      }}
      onSubmit={(values, {setSubmitting}) => {
        // Validation manuelle améliorée
        const errors = {};

        if (values.minBudget && !values.maxBudget) {
          errors.maxBudget = "Veuillez remplir le champ budget maximum";
        }
        if (values.maxBudget && !values.minBudget) {
          errors.minBudget = "Veuillez remplir le champ budget minimum";
        }

        if (Object.keys(errors).length > 0) {
          setSubmitting(false);
          setCustomErrors({...errors});
          return;
        }
        setCustomErrors({});

        // Logique de traitement
        let selectedSectors = [];

        if (
          !!values.selectedAll ||
          values.selectedSector?.length === sectors?.length ||
          values.selectedSector?.length === 0
        ) {
          selectedSectors = sectors?.length
            ? sectors.map((el) => parseInt(el.id))
            : [];
        } else {
          selectedSectors =
            values.selectedSector?.map((el) => parseInt(el)) || [];
        }

        const newFilter = {
          sectorIds: selectedSectors,
          statuses: values.selectedStatus,
        };

        if (values.maxBudget && values.minBudget) {
          newFilter.minBudget = {
            from: Number(values.minBudget),
            to: Number(values.maxBudget),
          };
        }

        setFilterTouched(true);
        setFilter((prev) => ({
          ...prev,
          order: [["id", values.selectedOrder]],
          filter: newFilter,
        }));

        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmit} className={className}>
          <div className="w-full p-6 flex flex-col gap-[14px] overflow-hidden relative bg-white rounded-2xl border border-solid border-[#E2E2E2]">
            <div className="flex flex-col gap-y-3 sm:flex-row justify-start items-start w-full">
              <div className="flex justify-start flex-col gap-2 sm:w-1/2">
                <div className="font-medium text-xs text-[#7e7e7e] mb-2">
                  {t("offerFilterSector")}
                </div>
                <label className="block text-[#595959] text-sm cursor-pointer">
                  <Field type="checkbox" name="selectedAll" className="mr-2" />
                  {t("allMySectors")}
                </label>
                {sectors.map((sector) => (
                  <label
                    key={sector.id}
                    className="block text-[#595959] text-sm cursor-pointer"
                  >
                    <Field
                      type="checkbox"
                      name="selectedSector"
                      value={sector?.id}
                      className="mr-2"
                      checked={
                        values.selectedSector.includes(String(sector?.id)) ||
                        values.selectedAll
                      }
                    />
                    {sector?.title}
                  </label>
                ))}
              </div>

              <div className="flex justify-start flex-col gap-2">
                <div className="font-medium text-xs text-[#7e7e7e] mb-2">
                  {t("filterByStatus")}
                </div>
                <label className="block text-[#595959] text-sm cursor-pointer">
                  <Field
                    type="checkbox"
                    name="selectedStatus"
                    value={offerStatus.PUBLIC}
                    className="mr-2"
                  />
                  {t("openOffers")}
                </label>
                <label className="block text-[#595959] text-sm cursor-pointer">
                  <Field
                    type="checkbox"
                    name="selectedStatus"
                    value={offerStatus.PROCESSING}
                    className="mr-2"
                  />
                  {t("offersInProgress")}
                </label>
              </div>
            </div>
            <div className="flex flex-col gap-y-3 sm:flex-row justify-start items-start w-full">
              <div className="flex justify-start flex-col gap-2 w-1/2">
                <div className="font-medium text-xs text-[#7e7e7e] mb-2">
                  {t("offerFilterBudget")}
                </div>
                <div className="flex justify-start gap-2 text-xs">
                  <div className="flex flex-col">
                    <label htmlFor="minBudget " className="text-[10px]">
                      {t("minimumBudget")}{" "}
                    </label>
                    <div className="flex rounded-lg p-2 border border-[#AEAEAE] border-solid w-[90px]">
                      <input
                        type="number"
                        className="border-none w-full "
                        name="minBudget"
                        onChange={handleChange}
                        value={values.minBudget}
                      />{" "}
                      <span className="text-[#1A64B9]">€</span>
                    </div>
                    {((touched.maxBudget && errors.minBudget) ||
                      !!customErrors?.minBudget) && (
                      <div
                        className="invalid-feedback animated fadeInUp"
                        style={{display: "block"}}
                      >
                        {errors.minBudget || customErrors.minBudget}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="maxBudget " className="text-[10px]">
                      {t("maximumBudget")}{" "}
                    </label>
                    <div className="flex rounded-lg p-2 border border-[#AEAEAE] border-solid w-[90px] ">
                      <input
                        type="number"
                        className="border-none w-full"
                        name="maxBudget"
                        onChange={handleChange}
                        value={values.maxBudget}
                      />{" "}
                      <span className="text-[#1A64B9]">€</span>
                    </div>
                    {((touched.maxBudget && errors.maxBudget) ||
                      !!customErrors.maxBudget) && (
                      <div
                        className="invalid-feedback animated fadeInUp"
                        style={{display: "block"}}
                      >
                        {errors.maxBudget || customErrors.maxBudget}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-start flex-col gap-2">
                <div className="font-medium text-xs text-[#7e7e7e] mb-2">
                  {t("sort")}
                </div>
                <label className="block text-[#595959] text-sm cursor-pointer">
                  <Field
                    type="radio"
                    name="selectedOrder"
                    value={"DESC"}
                    className="mr-2"
                  />
                  {t("mostRecent")}
                </label>
                <label className="block text-[#595959] text-sm cursor-pointer">
                  <Field
                    type="radio"
                    name="selectedOrder"
                    value={"ASC"}
                    className="mr-2"
                  />
                  {t("oldest")}
                </label>
              </div>
            </div>

            <div className="flex w-full justify-end">
              <div className="flex gap-3">
                <button
                  type="reset"
                  onClick={() => {
                    setFilterTouched(false);
                    resetForm();
                    setFilter(initialFilter);
                  }}
                  className="btn !px-5 !py-0 h-[47px]  rounded-lg text-[14px] flex justify-center items-center bg-white  !text-[#1A64B9] border-2 border-solid !border-[#1A64B9]  "
                >
                  {t("resetFilter")}
                </button>
                <button
                  type="submit"
                  className="btn !px-5 !py-0 h-[47px]  rounded-lg text-[14px] flex justify-center items-center bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white "
                >
                  {t("applyFilter")}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default FilterCard;
